import React, { useState } from 'react';
import { appToast } from '../../../../components/AppToast';

export default function SalarySlipFormatModal({ isOpen, onClose }) {
  const [selectedFormat, setSelectedFormat] = useState('standard');
  const [showLogo, setShowLogo] = useState(true);
  const [showAttendanceStats, setShowAttendanceStats] = useState(true);
  const [showBankDetails, setShowBankDetails] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const formats = [
    {
      id: 'standard',
      name: 'Standard Corporate Format',
      description: 'Two-column format with earnings, statutory deductions, net payable, and employee signature block.',
      icon: 'view_column',
    },
    {
      id: 'detailed',
      name: 'Comprehensive Tax Breakdown',
      description: 'Expanded layout displaying YTD earnings, tax deductions, PF calculations, and leave balance summary.',
      icon: 'format_align_left',
    },
    {
      id: 'compact',
      name: 'Compact Thermal / Email Slip',
      description: 'Optimized for quick digital distribution, thermal receipts, and mobile view.',
      icon: 'smartphone',
    },
  ];

  const handleSaveFormat = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      appToast.success('Salary Slip format preferences updated successfully!');
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl">description</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-on-surface">
                Salary Slip Format Configuration
              </h3>
              <p className="text-xs text-on-surface-variant font-medium">
                Choose the template and layout used for generating employee payslips.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface flex items-center justify-center transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Format Selector List */}
        <div className="space-y-3">
          {formats.map((fmt) => (
            <div
              key={fmt.id}
              onClick={() => setSelectedFormat(fmt.id)}
              className={`p-4 rounded-xl border transition cursor-pointer flex items-start gap-4 ${
                selectedFormat === fmt.id
                  ? 'border-primary bg-primary/5 shadow-xs'
                  : 'border-outline-variant/30 bg-surface-container-low hover:border-primary/40'
              }`}
            >
              <div className={`p-2.5 rounded-lg shrink-0 ${
                selectedFormat === fmt.id ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant'
              }`}>
                <span className="material-symbols-outlined text-xl">{fmt.icon}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-on-surface">{fmt.name}</h4>
                  {selectedFormat === fmt.id && (
                    <span className="material-symbols-outlined text-base text-primary font-bold">check_circle</span>
                  )}
                </div>
                <p className="text-[11px] text-on-surface-variant mt-1 leading-relaxed">{fmt.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Layout Options */}
        <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/60 p-4 space-y-3 text-xs">
          <span className="text-[10px] uppercase font-bold text-on-surface-variant tracking-wider">Include Sections in Payslip</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showLogo}
                onChange={(e) => setShowLogo(e.target.checked)}
                className="h-4 w-4 rounded text-primary focus:ring-primary/20 accent-primary"
              />
              <span className="font-semibold text-on-surface">Company Logo</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAttendanceStats}
                onChange={(e) => setShowAttendanceStats(e.target.checked)}
                className="h-4 w-4 rounded text-primary focus:ring-primary/20 accent-primary"
              />
              <span className="font-semibold text-on-surface">Attendance Stats</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showBankDetails}
                onChange={(e) => setShowBankDetails(e.target.checked)}
                className="h-4 w-4 rounded text-primary focus:ring-primary/20 accent-primary"
              />
              <span className="font-semibold text-on-surface">Bank A/c Details</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-on-surface text-xs font-bold transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveFormat}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-sm transition disabled:opacity-50 flex items-center gap-2 cursor-pointer"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">save</span>
                <span>Apply Format</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
